import { useState, useEffect } from "react";
import { FeatureDefinition } from "../api";

/* ── Dummy projects ── */
const PROJECTS = [
  { id: "proj_custody_outcomes", name: "Custody Outcome Prediction — Family Law / Divorce", org: "Family Justice Lab", varCount: 6 },
];

/* ── Dummy variables with full metadata & governance ── */
interface Variable {
  id: string;
  name: string;
  displayName: string;
  description: string;
  type: "Ordinal" | "Continuous" | "Binary" | "Categorical";
  levels?: string;
  source: "elicitation" | "data" | "proxy" | "imputed";
  sourceDetail: string;
  target: string;
  domain: string;
  owner: string;
  createdAt: string;
  updatedAt: string;
  status: "active" | "draft" | "deprecated" | "under_review";
  prior: { type: string; mean: number; std: number; certainty: number } | null;
  dataStats: { n: number; missing: string; mean?: number; std?: number; min?: number; max?: number } | null;
  governance: {
    pii: boolean;
    approved: boolean;
    approvedBy: string | null;
    reviewDate: string | null;
    lineage: string;
    documentation: string;
    tags: string[];
  };
  projectId: string;
}

const VARIABLES: Variable[] = [
  {
    id: "var_income_disparity",
    name: "Income_Disparity_Index",
    displayName: "Income Disparity Index",
    description: "Ratio of higher-earning to lower-earning parent's income, adjusted for cost of living and imputed income from assets. Expert-elicited weights reflect judicial tendency to favor stability for the child.",
    type: "Continuous",
    source: "elicitation",
    sourceDetail: "Expert elicitation (Sarah Hartwell, SHELF protocol)",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Sarah Hartwell",
    createdAt: "2026-01-10",
    updatedAt: "2026-03-13",
    status: "active",
    prior: { type: "Normal", mean: 0.35, std: 0.15, certainty: 0.78 },
    dataStats: { n: 12400, missing: "2.1%", mean: 0.38, std: 0.19 },
    governance: {
      pii: true,
      approved: true,
      approvedBy: "Hon. Rebecca Paulson",
      reviewDate: "2026-03-01",
      lineage: "COURT_RECORDS.FINANCIAL_DISCLOSURES -> INCOME_CALC -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "Derived from mandatory financial disclosure forms filed during divorce proceedings. Income imputation uses IRS W-2 matching where available. Adjusted for regional cost-of-living using BLS CPI data.",
      tags: ["financial", "expert-elicited", "continuous", "pii"],
    },
    projectId: "proj_custody_outcomes",
  },
  {
    id: "var_parental_involvement",
    name: "Parental_Involvement_Score",
    displayName: "Parental Involvement Score",
    description: "Composite of school attendance records, medical appointment participation, extracurricular involvement, and daily caregiving hours. Primary predictor of 'best interest of the child' determinations.",
    type: "Continuous",
    source: "data",
    sourceDetail: "CASE_MGMT.PARENT_ACTIVITY_LOGS joined with SCHOOL_DB.ATTENDANCE",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Data Engineering",
    createdAt: "2025-09-15",
    updatedAt: "2026-02-20",
    status: "active",
    prior: null,
    dataStats: { n: 18200, missing: "4.5%", mean: 0.62, std: 0.21, min: 0.05, max: 0.98 },
    governance: {
      pii: true,
      approved: true,
      approvedBy: "Dr. Michael Kwan",
      reviewDate: "2026-02-01",
      lineage: "SCHOOL_DB.ATTENDANCE -> JOIN MEDICAL_DB.APPTS -> COMPOSITE_SCORE -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "School records from participating districts. Medical appointment logs from pediatric providers with consent. Self-reported caregiving hours validated against time-use surveys.",
      tags: ["parental-behavior", "continuous", "validated", "pii"],
    },
    projectId: "proj_custody_outcomes",
  },
  {
    id: "var_domestic_violence",
    name: "Domestic_Violence_Risk_Score",
    displayName: "Domestic Violence Risk Score",
    description: "Validated risk assessment combining police reports, protection order history, and clinician-administered danger assessment. Critical safety variable with near-automatic custody implications.",
    type: "Continuous",
    source: "data",
    sourceDetail: "LAW_ENFORCEMENT.INCIDENT_REPORTS joined with COURT_DB.PROTECTION_ORDERS",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Dr. Diana Washington",
    createdAt: "2025-08-01",
    updatedAt: "2026-03-10",
    status: "active",
    prior: null,
    dataStats: { n: 15600, missing: "1.2%", mean: 0.18, std: 0.28, min: 0.0, max: 1.0 },
    governance: {
      pii: true,
      approved: true,
      approvedBy: "Sarah Hartwell",
      reviewDate: "2026-02-28",
      lineage: "LAW_ENFORCEMENT.INCIDENTS -> RISK_CALC -> JOIN COURT_DB.ORDERS -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "Combines Campbell Danger Assessment scores with verified police reports and protection order filings. Binary and continuous versions available. High sensitivity prioritized over specificity.",
      tags: ["safety", "continuous", "validated", "pii", "sensitive"],
    },
    projectId: "proj_custody_outcomes",
  },
  {
    id: "var_child_attachment",
    name: "Child_Attachment_Quality",
    displayName: "Child Attachment Quality",
    description: "Clinician-rated attachment security for each parent-child dyad using the Strange Situation Protocol or age-appropriate equivalent. Expert prior reflects developmental psychology consensus.",
    type: "Continuous",
    source: "elicitation",
    sourceDetail: "Expert elicitation (Dr. Michael Kwan, modified Delphi)",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Dr. Michael Kwan",
    createdAt: "2026-02-01",
    updatedAt: "2026-03-08",
    status: "active",
    prior: { type: "Normal", mean: 0.55, std: 0.11, certainty: 0.84 },
    dataStats: { n: 4200, missing: "8.3%", mean: 0.52, std: 0.14 },
    governance: {
      pii: true,
      approved: true,
      approvedBy: "Dr. Adriana Morales",
      reviewDate: "2026-03-05",
      lineage: "PSYCH_EVAL.ATTACHMENT_SCORES -> NORMALIZE -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "Attachment quality assessed via Strange Situation Protocol (ages 1-3), Attachment Q-Sort (ages 3-5), or Child Attachment Interview (ages 7+). Inter-rater reliability \u03BA=0.82. Expert weights capture nonlinear relationship between attachment and custody outcomes.",
      tags: ["psychological", "expert-elicited", "continuous", "clinical"],
    },
    projectId: "proj_custody_outcomes",
  },
  {
    id: "var_substance_abuse",
    name: "Substance_Abuse_History",
    displayName: "Substance Abuse History",
    description: "Composite of drug test results, treatment history, DUI records, and clinical assessment. Includes recovery duration as a moderating factor \u2014 courts weigh sustained sobriety differently from recent relapse.",
    type: "Continuous",
    source: "data",
    sourceDetail: "COURT_DB.DRUG_TESTS joined with TREATMENT_DB.RECORDS",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Data Engineering",
    createdAt: "2025-10-01",
    updatedAt: "2026-02-15",
    status: "active",
    prior: null,
    dataStats: { n: 9800, missing: "3.7%", mean: 0.22, std: 0.31, min: 0.0, max: 1.0 },
    governance: {
      pii: true,
      approved: true,
      approvedBy: "Dr. Diana Washington",
      reviewDate: "2026-02-10",
      lineage: "COURT_DB.DRUG_TESTS -> JOIN TREATMENT_DB -> COMPOSITE_RISK -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "Drug test results from court-ordered screenings. Treatment facility records with patient consent. DUI/DWI from DMV records. Recovery duration computed from last positive test to present.",
      tags: ["substance-abuse", "continuous", "court-ordered", "pii", "sensitive"],
    },
    projectId: "proj_custody_outcomes",
  },
  {
    id: "var_geographic_stability",
    name: "Geographic_Stability_Score",
    displayName: "Geographic Stability Score",
    description: "Measures residential stability, school district continuity, and proximity between parents' homes. High missing rate (7.8%) in cases involving relocation disputes. Under review for potential bias toward homeowners.",
    type: "Continuous",
    source: "data",
    sourceDetail: "COURT_DB.ADDRESS_HISTORY joined with SCHOOL_DB.ENROLLMENT",
    target: "Custody_Outcome_Score",
    domain: "Family Law",
    owner: "Data Engineering",
    createdAt: "2025-11-01",
    updatedAt: "2026-03-05",
    status: "under_review",
    prior: null,
    dataStats: { n: 11000, missing: "7.8%", mean: 0.58, std: 0.24, min: 0.0, max: 1.0 },
    governance: {
      pii: true,
      approved: false,
      approvedBy: null,
      reviewDate: null,
      lineage: "COURT_DB.ADDRESSES -> STABILITY_CALC -> JOIN SCHOOL_DB.ENROLLMENT -> ANALYTICS_DB.CUSTODY_FEATURES",
      documentation: "Address history from court filings. School enrollment continuity from district records. Proximity calculated via geocoding. Under review \u2014 potential homeowner bias may disadvantage lower-income parents in relocation disputes.",
      tags: ["geographic", "continuous", "under-review", "potential-bias"],
    },
    projectId: "proj_custody_outcomes",
  },
];

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "rgba(16,185,129,0.1)", text: "#059669", label: "Active" },
  draft: { bg: "rgba(99,102,241,0.1)", text: "#6366f1", label: "Draft" },
  deprecated: { bg: "rgba(239,68,68,0.1)", text: "#dc2626", label: "Deprecated" },
  under_review: { bg: "rgba(245,158,11,0.1)", text: "#d97706", label: "Under Review" },
};

const sourceColors: Record<string, { bg: string; text: string }> = {
  elicitation: { bg: "rgba(99,102,241,0.1)", text: "#6366f1" },
  data: { bg: "rgba(16,185,129,0.1)", text: "#059669" },
  proxy: { bg: "rgba(245,158,11,0.1)", text: "#d97706" },
  imputed: { bg: "rgba(239,68,68,0.1)", text: "#dc2626" },
};

interface VariablesTabProps {
  liveFeatures?: FeatureDefinition[];
}

export default function VariablesTab({ liveFeatures = [] }: VariablesTabProps) {
  const [selectedProject, setSelectedProject] = useState(PROJECTS[0].id);
  const [expandedVar, setExpandedVar] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("overview");
  const [expandedLive, setExpandedLive] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);

  // Flash animation when a new live feature arrives
  useEffect(() => {
    if (liveFeatures.length > 0) {
      const newest = liveFeatures[liveFeatures.length - 1];
      setFlashId(newest.feature_id);
      setExpandedLive(newest.feature_id);
      const timer = setTimeout(() => setFlashId(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [liveFeatures.length]);

  const project = PROJECTS.find(p => p.id === selectedProject)!;
  const variables = VARIABLES.filter(v => v.projectId === selectedProject);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{`
        @keyframes livePulse {
          0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); }
          70% { box-shadow: 0 0 0 8px rgba(99,102,241,0); }
          100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header with project dropdown */}
      <div style={{
        padding: "16px 28px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
        background: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Variable Catalog</h2>
            <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
              Governed variables with full lineage, metadata, and expert provenance
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", fontWeight: 500 }}>PROJECT</span>
            <select
              value={selectedProject}
              onChange={(e) => { setSelectedProject(e.target.value); setExpandedVar(null); }}
              style={{
                padding: "6px 28px 6px 12px",
                border: "1px solid rgba(0,0,0,0.12)", borderRadius: 8,
                fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                background: "#fff", color: "#000", outline: "none",
                cursor: "pointer",
                appearance: "none" as const,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23000' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
              }}
            >
              {PROJECTS.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Project summary row */}
        <div style={{ display: "flex", gap: 16, fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
          <span>Org: <strong style={{ color: "#000" }}>{project.org}</strong></span>
          <span>{variables.length} variables</span>
          <span>{variables.filter(v => v.source === "elicitation").length} expert-elicited</span>
          <span>{variables.filter(v => v.governance.approved).length} approved</span>
          <span>{variables.filter(v => v.status === "under_review").length} under review</span>
        </div>
      </div>

      {/* Variable list */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 28px 40px" }}>

        {/* ── Live Collected Features ── */}
        {liveFeatures.length > 0 && (
          <div style={{ marginBottom: 24, maxWidth: 800 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", background: "#6366f1",
                animation: "livePulse 2s infinite",
              }} />
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6366f1" }}>
                Live — Collected This Session ({liveFeatures.length})
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {liveFeatures.map((f) => {
                const isFlash = flashId === f.feature_id;
                const isExp = expandedLive === f.feature_id;
                return (
                  <div key={f.feature_id} style={{
                    border: "1px solid rgba(99,102,241,0.2)",
                    borderRadius: 10,
                    background: isFlash ? "rgba(99,102,241,0.04)" : "#fff",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    animation: "slideIn 0.4s ease",
                    ...(isExp ? { borderColor: "rgba(99,102,241,0.35)", boxShadow: "0 2px 12px rgba(99,102,241,0.08)" } : {}),
                  }}>
                    {/* Header row */}
                    <div
                      onClick={() => setExpandedLive(isExp ? null : f.feature_id)}
                      style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {f.name}
                          </span>
                          <span style={{
                            fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20,
                            background: "rgba(99,102,241,0.1)", color: "#6366f1",
                          }}>elicitation</span>
                          <span style={{
                            fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20,
                            background: "rgba(16,185,129,0.1)", color: "#059669",
                          }}>live</span>
                          <span style={{
                            fontSize: 9, fontWeight: 500, padding: "1px 8px", borderRadius: 20,
                            background: "rgba(0,0,0,0.04)", color: "rgba(0,0,0,0.45)",
                          }}>{f.encoding}</span>
                        </div>
                        <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginTop: 3 }}>
                          {f.source_logic.slice(0, 120)}{f.source_logic.length > 120 ? "..." : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 10, color: "rgba(99,102,241,0.8)",
                          fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                        }}>
                          {f.bayesian_prior.type}({f.bayesian_prior.mean.toFixed(2)}, {f.bayesian_prior.std_dev.toFixed(2)})
                        </span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"
                          style={{ opacity: 0.4, transition: "transform 0.2s", transform: isExp ? "rotate(180deg)" : "rotate(0)" }}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExp && (
                      <div style={{ padding: "0 16px 16px", animation: "fadeSlideIn 0.3s ease" }}>
                        {/* Hyperparameters grid */}
                        <div style={{
                          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                          gap: 8, marginBottom: 12,
                        }}>
                          {[
                            ["Distribution", f.bayesian_prior.type],
                            ["Prior μ", f.bayesian_prior.mean.toFixed(4)],
                            ["Prior σ", f.bayesian_prior.std_dev.toFixed(4)],
                            ["Certainty", `${(f.bayesian_prior.expert_certainty * 100).toFixed(0)}%`],
                            ...(f.bayesian_prior.alpha != null ? [["Alpha", f.bayesian_prior.alpha.toFixed(4)]] : []),
                            ...(f.bayesian_prior.beta != null ? [["Beta", f.bayesian_prior.beta.toFixed(4)]] : []),
                            ["Current Weight", f.current_weight.toFixed(4)],
                            ["Δ AUC", f.performance.delta_auc > 0 ? `+${f.performance.delta_auc.toFixed(4)}` : f.performance.delta_auc.toFixed(4)],
                            ["Shapley Value", `$${f.performance.shapley_value.toFixed(2)}`],
                          ].map(([label, val]) => (
                            <div key={label as string} style={{
                              background: "rgba(99,102,241,0.04)", borderRadius: 8, padding: "8px 12px",
                              border: "1px solid rgba(99,102,241,0.08)",
                            }}>
                              <div style={{
                                fontSize: 9, color: "rgba(99,102,241,0.6)", fontWeight: 600,
                                textTransform: "uppercase", letterSpacing: "0.04em",
                              }}>{label}</div>
                              <div style={{
                                fontSize: 13, fontWeight: 700, marginTop: 2,
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}>{val}</div>
                            </div>
                          ))}
                        </div>

                        {/* Source logic */}
                        <div style={{
                          padding: "10px 14px", borderRadius: 8,
                          background: "rgba(0,0,0,0.03)", marginBottom: 8,
                        }}>
                          <div style={{
                            fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                            color: "rgba(0,0,0,0.4)", marginBottom: 4,
                          }}>Expert Source Logic</div>
                          <div style={{
                            fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.65)",
                            fontStyle: "italic",
                          }}>"{f.source_logic}"</div>
                        </div>

                        {/* Mini prior visualization (text-based) */}
                        <div style={{
                          padding: "10px 14px", borderRadius: 8,
                          background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.1)",
                        }}>
                          <div style={{
                            fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                            color: "rgba(99,102,241,0.7)", marginBottom: 6,
                          }}>Prior Distribution</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {/* Simple bar visualization of the distribution spread */}
                            <div style={{ flex: 1, position: "relative", height: 32 }}>
                              <div style={{
                                position: "absolute", top: 14, left: 0, right: 0, height: 2,
                                background: "rgba(99,102,241,0.15)", borderRadius: 1,
                              }} />
                              {/* 1σ range */}
                              <div style={{
                                position: "absolute", top: 10, height: 10, borderRadius: 5,
                                background: "rgba(99,102,241,0.25)",
                                left: `${Math.max(0, 50 - 20)}%`,
                                width: "40%",
                              }} />
                              {/* Mean marker */}
                              <div style={{
                                position: "absolute", top: 6, width: 2, height: 18,
                                background: "#6366f1", left: "50%", borderRadius: 1,
                              }} />
                              <div style={{
                                position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                                fontSize: 9, fontWeight: 700, color: "#6366f1",
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}>{f.bayesian_prior.mean.toFixed(2)}</div>
                              <div style={{
                                position: "absolute", bottom: 0, left: `${Math.max(0, 50 - 20)}%`,
                                fontSize: 8, color: "rgba(99,102,241,0.5)",
                                fontFamily: "'IBM Plex Mono', monospace",
                              }}>{(f.bayesian_prior.mean - f.bayesian_prior.std_dev).toFixed(2)}</div>
                              <div style={{
                                position: "absolute", bottom: 0, right: `${Math.max(0, 50 - 20)}%`,
                                fontSize: 8, color: "rgba(99,102,241,0.5)",
                                fontFamily: "'IBM Plex Mono', monospace",
                                textAlign: "right",
                              }}>{(f.bayesian_prior.mean + f.bayesian_prior.std_dev).toFixed(2)}</div>
                            </div>
                            <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", minWidth: 80 }}>
                              ±1σ range<br />
                              <strong style={{ color: "#6366f1" }}>
                                [{(f.bayesian_prior.mean - f.bayesian_prior.std_dev).toFixed(2)}, {(f.bayesian_prior.mean + f.bayesian_prior.std_dev).toFixed(2)}]
                              </strong>
                            </div>
                          </div>
                        </div>

                        {/* Feature ID */}
                        <div style={{
                          marginTop: 8, fontSize: 10, color: "rgba(0,0,0,0.25)",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}>
                          id: {f.feature_id}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Catalog Variables ── */}
        {liveFeatures.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, maxWidth: 800 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.3)" }}>
              Catalog Variables
            </span>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.06)" }} />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 800 }}>
          {variables.map(v => {
            const isExpanded = expandedVar === v.id;
            const sc = statusColors[v.status];
            const src = sourceColors[v.source];

            return (
              <div key={v.id} style={{
                border: "1px solid rgba(0,0,0,0.08)", borderRadius: 10, background: "#fff",
                overflow: "hidden", transition: "all 0.2s ease",
                ...(isExpanded ? { borderColor: "rgba(0,0,0,0.18)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" } : {}),
              }}>
                {/* Row header */}
                <div
                  onClick={() => setExpandedVar(isExpanded ? null : v.id)}
                  style={{
                    padding: "12px 16px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 12,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{v.name}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20, background: sc.bg, color: sc.text }}>{sc.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 8px", borderRadius: 20, background: src.bg, color: src.text }}>{v.source}</span>
                      <span style={{ fontSize: 9, fontWeight: 500, padding: "1px 8px", borderRadius: 20, background: "rgba(0,0,0,0.04)", color: "rgba(0,0,0,0.45)" }}>{v.type}{v.levels ? ` (${v.levels})` : ""}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", marginTop: 3, lineHeight: 1.4 }}>
                      {v.description.slice(0, 100)}{v.description.length > 100 ? "..." : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    {v.prior && (
                      <span style={{ fontSize: 10, color: "rgba(99,102,241,0.8)", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>
                        N({v.prior.mean}, {v.prior.std})
                      </span>
                    )}
                    {v.dataStats && (
                      <span style={{ fontSize: 10, color: "rgba(0,0,0,0.35)", fontFamily: "'IBM Plex Mono', monospace" }}>
                        n={v.dataStats.n.toLocaleString()}
                      </span>
                    )}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                      style={{ opacity: 0.25, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", animation: "fadeSlideIn 0.3s ease" }}>
                    <style>{`@keyframes fadeSlideIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }`}</style>

                    {/* Section tabs */}
                    <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "1px solid rgba(0,0,0,0.06)", paddingBottom: 8 }}>
                      {["overview", "governance", "lineage", "statistics"].map(sec => (
                        <button key={sec} onClick={() => setActiveSection(sec)} style={{
                          padding: "4px 12px", borderRadius: 6, border: "none",
                          fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                          cursor: "pointer", fontFamily: "inherit",
                          background: activeSection === sec ? "#000" : "rgba(0,0,0,0.04)",
                          color: activeSection === sec ? "#fff" : "rgba(0,0,0,0.45)",
                          transition: "all 0.15s ease",
                        }}>{sec}</button>
                      ))}
                    </div>

                    {/* Overview */}
                    {activeSection === "overview" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.65)" }}>{v.description}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
                          {[
                            ["Type", `${v.type}${v.levels ? ` (${v.levels})` : ""}`],
                            ["Target", v.target],
                            ["Domain", v.domain],
                            ["Owner", v.owner],
                            ["Source", v.sourceDetail],
                            ["Created", v.createdAt],
                            ["Updated", v.updatedAt],
                            ["Status", sc.label],
                          ].map(([label, val]) => (
                            <div key={label as string} style={{ background: "rgba(0,0,0,0.03)", borderRadius: 8, padding: "8px 12px" }}>
                              <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{val}</div>
                            </div>
                          ))}
                        </div>
                        {v.prior && (
                          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(99,102,241,0.04)", border: "1px solid rgba(99,102,241,0.1)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(99,102,241,0.7)", marginBottom: 4 }}>Expert Prior</div>
                            <div style={{ display: "flex", gap: 16, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace" }}>
                              <span>Distribution: <strong>{v.prior.type}</strong></span>
                              <span>Mean: <strong>{v.prior.mean}</strong></span>
                              <span>Std: <strong>{v.prior.std}</strong></span>
                              <span>Certainty: <strong>{(v.prior.certainty * 100).toFixed(0)}%</strong></span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Governance */}
                    {activeSection === "governance" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div style={{
                            padding: "12px 16px", borderRadius: 8,
                            background: v.governance.approved ? "rgba(16,185,129,0.04)" : "rgba(245,158,11,0.04)",
                            border: `1px solid ${v.governance.approved ? "rgba(16,185,129,0.15)" : "rgba(245,158,11,0.15)"}`,
                          }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>Approval Status</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                width: 8, height: 8, borderRadius: "50%",
                                background: v.governance.approved ? "#10b981" : "#f59e0b",
                              }} />
                              <span style={{ fontSize: 13, fontWeight: 700 }}>{v.governance.approved ? "Approved" : "Pending"}</span>
                            </div>
                            {v.governance.approvedBy && (
                              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", marginTop: 4 }}>
                                By {v.governance.approvedBy} on {v.governance.reviewDate}
                              </div>
                            )}
                          </div>
                          <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>PII Classification</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{
                                width: 8, height: 8, borderRadius: "50%",
                                background: v.governance.pii ? "#ef4444" : "#10b981",
                              }} />
                              <span style={{ fontSize: 13, fontWeight: 700 }}>{v.governance.pii ? "Contains PII" : "No PII"}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 6 }}>Documentation</div>
                          <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(0,0,0,0.65)" }}>{v.governance.documentation}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 6 }}>Tags</div>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {v.governance.tags.map(tag => (
                              <span key={tag} style={{
                                fontSize: 10, padding: "2px 10px", borderRadius: 20,
                                background: "rgba(0,0,0,0.05)", color: "rgba(0,0,0,0.55)", fontWeight: 500,
                              }}>{tag}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Lineage */}
                    {activeSection === "lineage" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 8 }}>Data Lineage</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            {v.governance.lineage.split(" -> ").map((step, i, arr) => (
                              <span key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{
                                  fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
                                  padding: "4px 10px", borderRadius: 6, background: i === arr.length - 1 ? "rgba(0,0,0,0.08)" : "rgba(99,102,241,0.06)",
                                  color: i === arr.length - 1 ? "#000" : "rgba(99,102,241,0.8)",
                                }}>
                                  {step}
                                </span>
                                {i < arr.length - 1 && (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="2" strokeLinecap="round">
                                    <polyline points="9 6 15 12 9 18" />
                                  </svg>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>Source System</div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{v.sourceDetail}</div>
                          </div>
                          <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(0,0,0,0.03)" }}>
                            <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.4)", marginBottom: 4 }}>Target Variable</div>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{v.target}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Statistics */}
                    {activeSection === "statistics" && v.dataStats && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 8 }}>
                          {[
                            ["n", v.dataStats.n.toLocaleString()],
                            ["Missing", v.dataStats.missing],
                            ...(v.dataStats.mean !== undefined ? [["Mean", v.dataStats.mean.toFixed(3)]] : []),
                            ...(v.dataStats.std !== undefined ? [["Std Dev", v.dataStats.std.toFixed(3)]] : []),
                            ...(v.dataStats.min !== undefined ? [["Min", v.dataStats.min.toFixed(1)]] : []),
                            ...(v.dataStats.max !== undefined ? [["Max", v.dataStats.max.toFixed(1)]] : []),
                          ].map(([label, val]) => (
                            <div key={label as string} style={{ background: "rgba(16,185,129,0.04)", borderRadius: 8, padding: "8px 12px" }}>
                              <div style={{ fontSize: 9, color: "rgba(16,185,129,0.7)", fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
                              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>{val}</div>
                            </div>
                          ))}
                        </div>
                        {v.prior && v.dataStats.mean !== undefined && (
                          <div style={{
                            padding: "10px 14px", borderRadius: 8,
                            background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.1)",
                          }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(245,158,11,0.8)", marginBottom: 4 }}>Prior vs Data</div>
                            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.6)", lineHeight: 1.5 }}>
                              Expert prior mean ({v.prior.mean}) vs data mean ({v.dataStats.mean.toFixed(3)}) — delta of {Math.abs(v.prior.mean - v.dataStats.mean).toFixed(3)}. Expert uncertainty (sigma={v.prior.std}) is {v.dataStats.std !== undefined && v.prior.std > v.dataStats.std ? "wider" : "tighter"} than data ({v.dataStats.std?.toFixed(3)}).
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {activeSection === "statistics" && !v.dataStats && (
                      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.35)", padding: "20px 0", textAlign: "center" }}>
                        No data statistics available yet — run the pipeline to compute.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
